import type { EoePreviewPatientDemographics } from '../../../services/api/types.gen';

export interface InsuranceIdField {
  label: string;
  value: string;
  mono?: boolean;
  anchor?: string;
}

const displayValue = (value?: string | null): string =>
  value && value.trim() !== '' ? value : '-';

/**
 * Whether the patient identifier is shown as Member ID / Group number (sourced
 * from patient_insurance) rather than MBI. The backend
 * only populates member_id/group_number for Medicare plans, so their presence is
 * what drives the swap. Used to keep the readiness-check label/message consistent
 * with the field actually displayed.
 */
export function usesPatientInsuranceId(
  demographics: EoePreviewPatientDemographics | null | undefined
): boolean {
  return Boolean(
    demographics?.member_id?.trim() || demographics?.group_number?.trim()
  );
}

/**
 * The patient identifier fields for the claim worksheet / EOE preview.
 *
 * Medicare plans (per the patient_insurance linked to the billing cycle) bill with
 * the member id + group number from patient_insurance, so for those the single
 * MBI field is replaced with "Member ID" and "Group number". The
 * backend only populates member_id/group_number for Medicare plans, so their
 * presence is what drives the swap.
 */
export function buildInsuranceIdFields(
  demographics: EoePreviewPatientDemographics | null | undefined,
  anchor?: string
): InsuranceIdField[] {
  if (usesPatientInsuranceId(demographics)) {
    return [
      {
        label: 'Member ID',
        value: displayValue(demographics?.member_id),
        mono: true,
        anchor,
      },
      {
        label: 'Group number',
        value: displayValue(demographics?.group_number),
        mono: true,
      },
    ];
  }
  return [
    {
      label: 'MBI',
      value: displayValue(demographics?.mbi),
      mono: true,
      anchor,
    },
  ];
}
